import { getAppearance, setAppearance } from '../../core/appearance.js';
// ================================================================
// authShell — der gemeinsame Boss Pilot-Rahmen der öffentlichen Seiten
// ================================================================
// Product authentication shares one responsive frame. Customer documents retain
// their separate company branding and presentation through the cobrand variant.

import { PRODUCT } from '../../core/productIdentity.js';
import { t, getLanguage, setLanguage, getAvailableLanguages } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';

const languageOwners = new WeakMap();

/** Shared product auth frame. Public documents set cobrand before their tenant
 * identity has loaded, so no application wordmark flashes above a customer's quote. */
export function authShell(cardHtml, opts = {}) {
  const langs = getAvailableLanguages();
  const current = getLanguage();
  const cobrand = opts.cobrand || !!opts.brand;
  const variant = cobrand ? 'customer' : 'product';
  const brand = opts.brand || (cobrand ? '' : `<img class="auth-product-logo" src="${PRODUCT.wordmark}" alt="${escapeHtml(t('app.title'))}" />`);
  return `
    <div class="auth-gate ${variant === 'product' ? 'auth-product' : 'pm pm-cobrand'}" data-auth-variant="${variant}">
      <div class="auth-lang">
        ${!cobrand ? `<div class="appearance-control" role="group" aria-label="${escapeHtml(t('appearance.title'))}">
          ${['light', 'dark'].map(mode => `<button type="button" data-appearance-option="${mode}" data-auth-focus="appearance-${mode}" aria-pressed="${getAppearance() === mode}" aria-label="${escapeHtml(t(`appearance.${mode}`))}" title="${escapeHtml(t(`appearance.${mode}`))}">${icon(mode === 'light' ? 'light_mode' : 'dark_mode', 20)}</button>`).join('')}
        </div>` : ''}
        <select class="auth-lang-select" data-lang-select data-auth-focus="language" aria-label="${escapeHtml(t('onboarding.steps.language'))}">
          ${langs.map((l) => `
            <option value="${l.code}" ${l.code === current ? 'selected' : ''}>${escapeHtml(l.name)}</option>
          `).join('')}
        </select>
      </div>

      <div class="auth-shell">
        <div class="auth-brandspace">${brand}</div>
        <main class="auth-card" ${opts.busy ? 'aria-busy="true"' : ''}>${cardHtml}</main>
        <p class="auth-secure">${icon('lock', 15)}<span>${escapeHtml(t('auth.secureNote'))}</span></p>
      </div>
    </div>`;
}

/**
 * Hängt den Sprachumschalter an. `redraw` wird nach dem Laden der Sprachdatei
 * gerufen (die Dateien werden lazy geladen — vorher zu zeichnen zeigt die alte
 * Sprache).
 */
export function bindLangSelect(rootEl, redraw) {
  languageOwners.set(rootEl, redraw);
  rootEl.querySelectorAll('[data-appearance-option]').forEach(button => {
    button.addEventListener('click', () => setAppearance(button.dataset.appearanceOption));
  });
  rootEl.querySelector('[data-lang-select]')?.addEventListener('change', async (e) => {
    await setLanguage(e.target.value);
    // A request may finish while the catalog loads. Redraw the latest auth
    // state, but never resurrect a gate after routing into the application.
    if (languageOwners.get(rootEl) === redraw && rootEl.querySelector('[data-lang-select]')) redraw();
  });
}

/**
 * Zeichnet neu, ohne dem Nutzer das Getippte wegzunehmen.
 *
 * Die öffentlichen Seiten sind Vanilla: JEDER Zustandswechsel — Fehlermeldung,
 * Auge zum Passwort-Anzeigen, Sprachwechsel — ersetzt die Karte per innerHTML.
 * Die Felder stehen danach leer da. Im Alltag heisst das: man tippt sein
 * Passwort, klickt aufs Auge um zu prüfen, ob man sich vertippt hat — und alles
 * ist weg. Oder man vertippt sich beim Login und darf auch die E-Mail neu
 * eingeben. Genau an der Stelle, an der jemand ohnehin schon kämpft.
 *
 * Darum: Werte und Cursor VOR dem Neuzeichnen einsammeln, danach zurückschreiben
 * — adressiert über `name`. Überschrieben wird nur, was die neue Ansicht leer
 * lässt; setzt sie ein Feld absichtlich (Vorbelegung), gewinnt sie.
 */
export function redrawKeepingInput(rootEl, render) {
  const fields = () => rootEl.querySelectorAll('input[name], textarea[name]');
  const before = new Map();
  const active = document.activeElement;
  const focusKey = rootEl.contains(active) ? active?.getAttribute('data-auth-focus') : null;
  let focusName = null;
  let selStart = null;
  let selEnd = null;

  for (const el of fields()) {
    before.set(el.name, el.value);
    if (el === document.activeElement) {
      focusName = el.name;
      selStart = el.selectionStart;
      selEnd = el.selectionEnd;
    }
  }

  const scrollTop = rootEl.querySelector('.auth-gate')?.scrollTop || 0;
  render();

  for (const el of fields()) {
    if (!el.value && before.has(el.name)) el.value = before.get(el.name);
  }

  if (focusName) {
    const el = rootEl.querySelector(`[name="${CSS.escape(focusName)}"]`);
    if (el) {
      el.focus({ preventScroll: true });
      // setSelectionRange wirft bei type=email/number — dort reicht der Fokus.
      try { el.setSelectionRange(selStart, selEnd); } catch { /* egal */ }
    }
  }
  if (focusKey) rootEl.querySelector(`[data-auth-focus="${CSS.escape(focusKey)}"]`)?.focus({ preventScroll: true });
  const scroller = rootEl.querySelector('.auth-gate');
  if (scroller) scroller.scrollTop = scrollTop;
}
